import { localize } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import React, { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { ANDROID_STORE_URL, IOS_STORE_URL } from '../utils/storeLinks';

const CUSTOM_SCHEME_URL = 'pillreminder://family-care?role=ward';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);

export default function InvitePage() {
    const navigate = useNavigate();
    const [showPage, setShowPage] = useState(false);

    useEffect(() => {
        // 앱 내 WebView에서 열린 경우 → 바로 코드 생성 화면으로
        if (window.IS_NATIVE_APP) {
            navigate('/family-care?role=ward', { replace: true });
            return;
        }

        // 모바일 브라우저에서 열린 경우 → 커스텀 스킴으로 앱 열기 시도
        if (isIOS || isAndroid) {
            // iframe으로 커스텀 스킴 시도 (에러 페이지 방지)
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = CUSTOM_SCHEME_URL;
            document.body.appendChild(iframe);
            const iframeTimer = setTimeout(() => iframe.remove(), 500);

            // 앱이 열리면 페이지를 떠남 → visibilitychange 감지
            const onVisibility = () => {
                if (!document.hidden) setShowPage(true);
            };
            document.addEventListener('visibilitychange', onVisibility);

            // 1.5초 후에도 페이지에 남아있으면 → 앱 미설치로 판단
            const fallbackTimer = setTimeout(() => setShowPage(true), 1500);
            return () => {
                clearTimeout(iframeTimer);
                clearTimeout(fallbackTimer);
                document.removeEventListener('visibilitychange', onVisibility);
                iframe.remove();
            };
        } else {
            // PC 브라우저
            setShowPage(true);
        }
    }, [navigate]);

    const handleOpenApp = () => {
        window.location.href = CUSTOM_SCHEME_URL;
    };

    const handleStore = () => {
        if (isIOS) {
            window.location.href = IOS_STORE_URL;
        } else {
            window.location.href = ANDROID_STORE_URL;
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh', bgcolor: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            px: 3, textAlign: 'center',
            opacity: showPage ? 1 : 0, transition: 'opacity 0.3s',
        }}>
            <Box sx={{
                width: 80, height: 80, borderRadius: '20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3,
                overflow: 'hidden',
            }}>
                <Box
                    component="img"
                    src="/icon.png"
                    alt={localize("집요한약알림")}
                    sx={{ width: '100%', height: '100%', display: 'block' }}
                />
            </Box>

            <SingleLineText sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#1a1a1a', mb: 1 }}>{localize("집요한약알림")}</SingleLineText>

            <Typography sx={{ fontSize: '1.05rem', color: '#777', lineHeight: 1.6, mb: 4 }}>{localize("가족이 내 약 복용을 확인할 수 있도록")}<br />{localize("앱에서 연결 코드를 만들어주세요")}</Typography>

            {(isIOS || isAndroid) ? (
                <>
                    <Button
                        variant="contained" fullWidth
                        onClick={handleOpenApp}
                        sx={{
                            bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                            py: 1.8, fontWeight: 700, fontSize: '1.15rem',
                            '&:hover': { bgcolor: '#e8d040' },
                            maxWidth: 340, mb: 1.5,
                        }}
                    >{localize("앱 열기")}</Button>
                    <SingleLineText
                        onClick={handleStore}
                        role="button"
                        tabIndex={0}
                        sx={{ fontSize: '0.95rem', color: '#999', cursor: 'pointer', textDecoration: 'underline' }}
                    >{localize("앱이 없으신가요? 설치하기")}</SingleLineText>
                </>
            ) : (
                <SingleLineText sx={{ fontSize: '0.95rem', color: '#aaa' }}>{localize("모바일에서 링크를 열어주세요")}</SingleLineText>
            )}
        </Box>
    );
}
